import { fetchAccount, patchAccountProfile } from "../services/accountApi.js";
import React, { useState, useEffect } from "react";
import { C } from "../lib/colors.js";
import { redirectToLogin } from "../lib/apiClient.js";
import { beginIntent, ui } from "../lib/telemetry/instrument.js";
import { Button, Chip, Skeleton } from "./ui/Primitives.jsx";
import { IconCheck, IconExternal, IconPencil } from "./ui/Icons.jsx";
import { product as brandProduct } from "../branding.js";

/** Two initials for the avatar, from a display name or an email. */
function initialsOf(name, email) {
  const src = (name || "").trim() || (email || "").split("@")[0] || "";
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  const two = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : src.slice(0, 2);
  return two.toUpperCase();
}

// Account identity section: who you are (avatar, editable display name, email), then links to
// the identity provider for the sensitive changes (password, email, two-step sign-in). Display
// name is the only field Sayer updates directly via /api/account; everything else stays with
// the identity provider. When /api/account is unreachable it falls back to the session's own
// name and email rather than an error, with editing switched off.
function AccountIdentity({ me }) {
  const [acct, setAcct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [reauth, setReauth] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetchAccount();
        if (r.status === 401) return setReauth(true);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        setAcct(d);
        setDraft(d.displayName || "");
      } catch (e) {
        setErr(e.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  if (loading)
    return (
      <div style={{ display: "flex", gap: 14, alignItems: "center", padding: "8px 0 20px" }}>
        <Skeleton width={44} height={44} style={{ borderRadius: 22 }} />
        <div style={{ flex: 1 }}>
          <Skeleton width={160} height={14} />
          <Skeleton width={220} height={12} style={{ marginTop: 8 }} />
        </div>
      </div>
    );
  if (reauth)
    return (
      <div style={{ color: C.grayL, fontSize: 13, padding: "16px 0", textAlign: "center" }}>
        Your sign-in has expired.{" "}
        <Button
          variant="ghost"
          size="sm"
          {...ui("account.sign-in-again")}
          onClick={() => redirectToLogin()}
        >
          Sign in again
        </Button>
      </div>
    );
  // Unreachable account details: show what the session already knows, read-only.
  const offline = !!err || !acct;
  const displayName = offline ? me?.name || "" : acct.displayName || "";
  const email = offline ? me?.email || "" : acct.email || me?.email || "";
  const consoleUrl = acct?.consoleUrl || "https://auth.soothlabs.com/ui/console/users/me";
  const save = async () => {
    if (!draft.trim() || draft.trim() === acct.displayName) return setEditing(false);
    setSaving(true);
    const intent = beginIntent(
      "account.rename",
      {},
      { origin: "account.name-save", surface: "account" },
    );
    try {
      const r = await patchAccountProfile({ displayName: draft.trim() }, { intentId: intent.id });
      if (!r.ok) {
        intent.end("error");
        const e = await r.json().catch(() => ({}));
        if (r.status === 401) return setReauth(true);
        throw new Error(e.error || `HTTP ${r.status}`);
      }
      const d = await r.json();
      setAcct((a) => ({ ...a, displayName: d.displayName || draft.trim() }));
      setEditing(false);
      intent.end("ok");
    } catch (e) {
      intent.end("error");
      setErr(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };
  const manageLinks = [
    ["password", `${consoleUrl}?id=security`, "Change password"],
    ["email", consoleUrl, "Change email"],
    ["security", `${consoleUrl}?id=security`, "Two-step sign-in"],
  ];
  return (
    <div>
      {/* Who you are: the avatar, the (editable) display name and the email. */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "4px 0 20px" }}>
        <div
          aria-hidden="true"
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: C.rowActive,
            border: `1px solid ${C.border}`,
            color: C.accent,
            fontSize: 15,
            fontWeight: 600,
          }}
        >
          {initialsOf(displayName, email)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {!editing ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, minHeight: 28 }}>
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: displayName ? C.white : C.gray,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayName || "No display name"}
              </span>
              {!offline && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<IconPencil />}
                  aria-label="Edit display name"
                  {...ui("account.name-edit")}
                  onClick={() => {
                    setDraft(acct.displayName || "");
                    setEditing(true);
                  }}
                >
                  Edit
                </Button>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                {...ui("account.name-input")}
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                disabled={saving}
                maxLength={200}
                aria-label="Display name"
                onKeyDown={(e) => {
                  if (e.key === "Enter") save();
                  if (e.key === "Escape") setEditing(false);
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: C.inputBg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  color: C.white,
                  fontSize: 13,
                  padding: "6px 10px",
                  outline: "none",
                }}
              />
              <Button
                size="sm"
                variant="primary"
                {...ui("account.name-save")}
                onClick={save}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                {...ui("account.name-cancel")}
                onClick={() => {
                  setEditing(false);
                  setDraft(acct.displayName || "");
                }}
              >
                Cancel
              </Button>
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 2,
              fontSize: 13,
              color: C.gray,
              minWidth: 0,
            }}
          >
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
              {email || "No email on file"}
            </span>
            {acct?.emailVerified && (
              <Chip>
                <IconCheck /> Verified
              </Chip>
            )}
            {acct && !acct.emailVerified && acct.email && <Chip>Not verified</Chip>}
          </div>
          {acct?.preferredLoginName && acct.preferredLoginName !== email && (
            <div style={{ fontSize: 12, color: C.gray, marginTop: 2 }}>
              Signs in as {acct.preferredLoginName}
            </div>
          )}
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, marginTop: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.white, marginBottom: 4 }}>
          Manage my {brandProduct.prose} account
        </div>
        <div style={{ fontSize: 12, color: C.gray, marginBottom: 12 }}>
          These open your sign-in settings in a new tab.
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {manageLinks.map(([key, href, text]) => (
            <a
              key={key}
              {...ui("account.manage-link", key)}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="sy-btn sy-btn--sm"
            >
              {text}
              <IconExternal />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AccountIdentity;
